import { useEffect, useState } from 'react';
import ModalFrame from './modals/ModalFrame';
import { getApiErrorMessage } from '../utils/apiErrorMessage';

const MIN_PASSWORD_LENGTH = 6;

/**
 * Dedicated password reset — does not go through the full user update form.
 */
const ChangePasswordModal = ({ isOpen, onClose, student, onSubmit }) => {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setPassword('');
    setConfirm('');
    setError('');
    setSubmitting(false);
  }, [isOpen, student?.id]);

  const displayName =
    student?.fullName ||
    student?.full_name ||
    `${student?.firstName || ''} ${student?.lastName || ''}`.trim() ||
    student?.emailAddress ||
    student?.email ||
    'this user';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters long`);
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match');
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit(password);
      onClose();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Could not update password'));
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen || !student) return null;

  return (
    <ModalFrame
      isOpen
      onClose={onClose}
      titleId="change-password-title"
      title="Change password"
      description={`Set a new login password for ${displayName}.`}
      footer={
        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="change-password-form"
            disabled={submitting}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {submitting ? 'Saving…' : 'Update password'}
          </button>
        </div>
      }
    >
      <form id="change-password-form" onSubmit={handleSubmit} className="space-y-3">
        {error ? (
          <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
            {error}
          </p>
        ) : null}
        <label className="block">
          <span className="block text-sm font-medium text-gray-700 mb-1">New password</span>
          <input
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            minLength={MIN_PASSWORD_LENGTH}
            className="w-full px-3 py-2 border rounded"
          />
        </label>
        <label className="block">
          <span className="block text-sm font-medium text-gray-700 mb-1">Confirm password</span>
          <input
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            minLength={MIN_PASSWORD_LENGTH}
            className="w-full px-3 py-2 border rounded"
          />
        </label>
      </form>
    </ModalFrame>
  );
};

export default ChangePasswordModal;
